<script setup>
import {defineProps, ref, onMounted, defineEmits, computed} from 'vue'
import TheModal from '@shared-components/ui/modals/TheModal.vue'
import BaseForm from '@shared-components/base/BaseForm.vue'
import ApiErrors from '@shared-components/ui/ApiErrors.vue'
import {useOtherRequestStore} from '@/views/ad-account-requests/other-requests/OtherRequestStore'
import {storeToRefs} from 'pinia'
import {useAuthenticationStore, useDefaultStore} from '@/stores'
import InputPreloader from '@shared-components/ui/inputs/InputPreloader.vue'
import TheSelect from '@shared-components/ui/selects/TheSelect.vue'
import {useNotification} from '@kyvg/vue3-notification'
import TheSelectBusinessManagers from '@/views/assets/business-managers/components/TheSelectBusinessManagers.vue'
import {parseArrayToCommaJoinString} from '@shared-utils/helpers'
import TheSelectEmails from '@shared-components/ui/selects/TheSelectEmails.vue'
import OtherRequestGoogleVerificationForm from '@/views/ad-account-requests/other-requests/components/OtherRequestGoogleVerificationForm.vue'
import OtherRequestFacebookPageAccessForm from '@/views/ad-account-requests/other-requests/components/OtherRequestFacebookPageAccessForm.vue'
import {useMetaBusinessManagerStore} from '@/views/ad-account-requests/accounts/meta/MetaBusinessManagerStore'

const {notify} = useNotification()
const {createOtherRequest, getOtherRequest} = useOtherRequestStore()
const {typeOptions} = storeToRefs(useOtherRequestStore())
const {userCompanyHash} = storeToRefs(useAuthenticationStore())
const {pingBackend} = useDefaultStore()
const {getFacebookBusinessManagerSharedEmails} = useMetaBusinessManagerStore()

const bmTransferRequestType = 'BM Accounts Migration'
const bmTransferDescription =
  'Use this request if your current Facebook Business Manager was restricted or you need to replace it. ' +
  'We will transfer all available assets to a new Business Manager and restore access for the selected email addresses.'

// Props & emits
const props = defineProps({
  activeRequest: {
    type: Object,
    required: false,
  },
  id: {
    type: String,
    default: null,
  },
  requestType: {
    type: String,
    default: null,
  },
})
const emit = defineEmits(['hidden', 'created'])
const modalId = props.id ?? 'other-request-modal'

// Refs
const theModal = ref()
const defaultFormData = {
  company_hash: userCompanyHash.value ?? '',
  request_type: props.requestType ?? '',
  comment: '',
}
const defaultGoogleBusinessVerificationFormData = {
  meta: {
    geo: '',
    website_url: '',
    business_registration_link: undefined,
    license_link: undefined,
    loa_link: undefined,
    additional_link: undefined,
  },
  file_hashes: [],
  platform: 'google',
  ad_account_title: undefined,
  ad_account_ids: '',
}
const defaultBmTransferFormData = {
  share_to: [],
  business_manager_hash: undefined,
  business_manager_title: undefined,
}
const defaultFBPageAccessShareFormData = {
  platform: 'facebook',
  ad_account_ids: [],
  pages_mode: '',
  page_hashes: [],
  page_urls: [''],
  access_type: '',
  business_manager_hash: undefined,
  profile_urls: [''],
}
const bmTransferFormData = ref(cloneSafe(defaultBmTransferFormData))
const fbPageAccessShareFormData = ref(cloneSafe(defaultFBPageAccessShareFormData))
const facebookPageAccessForm = ref()
const isFacebookPageAccessFormValid = ref(true)
const googleBusinessVerificationFormData = ref(cloneSafe(defaultGoogleBusinessVerificationFormData))
const googleVerificationForm = ref()
const formData = ref(cloneSafe(defaultFormData))
const loading = ref(false)
const requestLoading = ref(false)
const sharedEmailsLoading = ref(false)
const error = ref(null)
const selectBusinessManagers = ref()

// Computed
const modalTitle = computed(() => {
  if (props.activeRequest) {
    return `Request #${props.activeRequest.hash}`
  }

  const selectedType = typeOptions.value.find(option => option.value === props.requestType)

  return selectedType?.label ?? 'Other Request'
})
const isCommentFieldRequired = computed(
  () => !isFbPageAccessShare.value && !isGoogleBusinessVerification.value && !isBmTransfer.value,
)
const commentFieldLabel = computed(() => (isBmTransfer.value ? 'Additional Comments' : 'Comment'))
const isFbPageAccessShare = computed(() => formData.value.request_type === 'FB Page Access Share')
const isBmTransfer = computed(() => formData.value.request_type === bmTransferRequestType)
const isGoogleBusinessVerification = computed(() => formData.value.request_type === 'Advertiser Verification')
const isSubmitDisabled = computed(
  () => loading.value || (isFbPageAccessShare.value && !isFacebookPageAccessFormValid.value),
)

// Methods
onMounted(() => {
  init()

  openModal()
})

const init = () => {
  if (props.activeRequest) {
    handleGetRequest()

    return
  }

  formData.value = cloneSafe(defaultFormData)
}
const openModal = () => {
  theModal.value.handleOpenModal()
}

const closeModal = () => {
  theModal.value.handleCloseModal()
}

const mapVerificationFiles = (target, files) => {
  const defaults = ['file_tempo_business_registration', 'file_tempo_verification_loa']
  const mapping = {
    file_tempo_business_registration: 'advertiser-verification-business-registration',
    file_tempo_verification_license: 'advertiser-verification-license',
    file_tempo_verification_loa: 'advertiser-verification-loa',
    file_tempo_verification_additional: 'advertiser-verification-additional',
  }

  Object.entries(mapping).forEach(([key, usage]) => {
    target[key] = files.find(f => f.usage_type === usage) || (defaults.includes(key) ? {hash: undefined} : undefined)
  })
}

const handleGetRequest = async () => {
  requestLoading.value = true

  try {
    let response = await getOtherRequest(props.activeRequest?.hash, {
      with: ['company', 'user', 'responsible', 'files', 'entities.entity.business_manager'],
    })

    await handleFillFormData(response)
  } catch (responseError) {
    error.value = responseError?.data
  } finally {
    requestLoading.value = false
  }
}

const handleFillFormData = async data => {
  formData.value = cloneSafe(data)
  formData.value.company_hash = data.company.hash

  if (isBmTransfer.value) {
    Object.assign(bmTransferFormData.value, data)

    const linkedEntity = data.entities?.[0]

    bmTransferFormData.value.business_manager_title = linkedEntity?.business_manager?.title ?? linkedEntity?.title
  }

  if (isGoogleBusinessVerification.value) {
    googleBusinessVerificationFormData.value.meta = cloneSafe(data.meta)
    googleBusinessVerificationFormData.value.website_url = data.meta.website_url
    googleBusinessVerificationFormData.value.ad_account_title = data.entities[0]?.title

    mapVerificationFiles(googleBusinessVerificationFormData.value, data.files)
  }
}

const getRequestPayload = () => {
  let payload = cloneSafe(formData.value)

  if (isGoogleBusinessVerification.value) {
    payload.platform = 'google'
    payload = Object.assign(payload, googleBusinessVerificationFormData.value)

    for (const key in payload) {
      if (key.startsWith('file_tempo_')) {
        delete payload[key]
      }
    }

    payload.file_hashes = googleVerificationForm.value?.formatFilesToPayload()
    payload.ad_account_ids = [payload.ad_account_ids]

    return payload
  }

  if (isFbPageAccessShare.value) {
    return {...payload, ...facebookPageAccessForm.value.getPayload()}
  }

  if (isBmTransfer.value) {
    payload = cloneSafe({...bmTransferFormData.value, ...payload})
    payload.share_to = parseArrayToCommaJoinString(payload.share_to)
    delete payload.business_manager_title
  }

  return payload
}

const handleSubmit = async () => {
  loading.value = true

  let response

  try {
    response = await createOtherRequest(getRequestPayload())
    pingBackend()
  } catch (responseError) {
    error.value = responseError?.data
  } finally {
    loading.value = false
  }

  if (!response) {
    return
  }

  emit('created')
  notify({type: 'success', text: 'Request created!'})
  closeModal()
}

const onBusinessManagerChange = async () => {
  bmTransferFormData.value.share_to = []

  const businessManagerHash = bmTransferFormData.value.business_manager_hash

  if (!businessManagerHash) {
    return
  }

  sharedEmailsLoading.value = true

  try {
    const response = await getFacebookBusinessManagerSharedEmails(businessManagerHash)

    if (bmTransferFormData.value.business_manager_hash === businessManagerHash) {
      bmTransferFormData.value.share_to = response?.data ?? []
    }
  } catch (responseError) {
    error.value = responseError?.data
  } finally {
    sharedEmailsLoading.value = false
  }
}

const emitHidden = () => {
  emit('hidden')
}
</script>

<template>
  <TheModal ref="theModal" @hidden="emitHidden" :id="modalId" :title="modalTitle">
    <base-form @on-submitted="handleSubmit">
      <div class="modal-body">
        <div v-if="!props.requestType" class="form-group">
          <label>Request Type *</label>
          <input-preloader :show-preloader="requestLoading && !formData.request_type" name="Request type">
            <the-select
              :options="typeOptions"
              v-model="formData.request_type"
              :disabled="!!props.activeRequest"
              :allow_clear="false"
              :can_deselect="false"
              :searchable="false"
              required
            />
          </input-preloader>
        </div>
        <template v-if="isFbPageAccessShare">
          <input-preloader :show-preloader="requestLoading" name="Request">
            <other-request-facebook-page-access-form
              v-model="fbPageAccessShareFormData"
              v-model:is-valid="isFacebookPageAccessFormValid"
              :is-creating="!props.activeRequest"
              :company-hash="formData.company_hash"
              :page-access="formData.page_access"
              :ad-account="formData.entities?.[0]"
              :legacy-pages="formData.entity_to_share"
              :legacy-profiles="formData.share_to"
              ref="facebookPageAccessForm"
            />
          </input-preloader>
        </template>
        <template v-else-if="isBmTransfer">
          <p v-if="!props.activeRequest" class="text-muted">{{ bmTransferDescription }}</p>
          <div class="form-group">
            <label>Current Facebook Business Manager *</label>
            <the-select-business-managers
              v-if="!props.activeRequest"
              v-model="bmTransferFormData.business_manager_hash"
              :list-by-search="!!userCompanyHash"
              platform="meta"
              :payload="{
                company_hash: formData.company_hash,
              }"
              :disabled="formData.company_hash === undefined"
              :with-appended="false"
              @change="onBusinessManagerChange"
              mode="single"
              ref="selectBusinessManagers"
              required
            >
            </the-select-business-managers>
            <input v-else class="form-control" :value="bmTransferFormData.business_manager_title" disabled />
            <small v-if="!props.activeRequest" class="text-muted">
              Select the Business Manager that needs to be replaced.
            </small>
          </div>
          <div class="form-group">
            <label>Emails to Restore Access</label>
            <input-preloader v-if="!props.activeRequest" :show-preloader="sharedEmailsLoading" name="Emails">
              <the-select-emails
                v-model="bmTransferFormData.share_to"
                :disabled="!bmTransferFormData.business_manager_hash || sharedEmailsLoading"
                placeholder="Select a Business Manager to load its emails"
              />
            </input-preloader>
            <input v-else class="form-control" :value="bmTransferFormData.share_to" disabled />
            <small v-if="!props.activeRequest" class="text-muted">
              All emails linked to the selected Business Manager are preselected. Remove the ones that should not get
              access to the new Business Manager.
            </small>
          </div>
        </template>

        <other-request-google-verification-form
          v-else-if="isGoogleBusinessVerification"
          v-model="googleBusinessVerificationFormData"
          v-model:main-form-data="formData"
          :is-creating="!props.activeRequest"
          ref="googleVerificationForm"
        />
        <div class="form-group">
          <label for="other-request-modal-comment"
            >{{ commentFieldLabel }}<span v-if="isCommentFieldRequired"> *</span></label
          >
          <input-preloader :show-preloader="requestLoading && !formData.comment" name="Comment">
            <textarea
              v-model="formData.comment"
              id="other-request-modal-comment"
              class="form-control"
              :disabled="!!props.activeRequest"
              rows="3"
              :required="isCommentFieldRequired"
            ></textarea>
          </input-preloader>
        </div>

        <div v-if="error" class="form-group">
          <api-errors :error="error" />
        </div>
      </div>
      <div class="modal-footer justify-content-end custom gap-4">
        <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
        <button v-if="!props.activeRequest" class="btn btn-primary" :disabled="isSubmitDisabled">Submit</button>
      </div>
    </base-form>
  </TheModal>
</template>
